import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { CircularProgress } from '@mui/material';
import { useRouter } from 'next/router';
import { updateInstantWinEvent } from 'ducks/client/instantWinEvent';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ReduxState } from 'ducks';
import { InstantWinEvent } from 'models/instantWinEvent';

import styles from './DrawTypeRoulette.module.css';
import rouletteStyles from './Roulette.module.css';

const Wheel = dynamic(() => import('react-custom-roulette').then((mod) => mod.Wheel), {
  ssr: false,
});

interface Props {
  instantWinEvent: InstantWinEvent;
  onTierKeyChange: (tierKey: string) => void;
  onTierModalOpen: () => void;
}

export const DrawTypeRoulette = ({
  instantWinEvent,
  onTierKeyChange,
  onTierModalOpen,
}: Props): any => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { apiKey } = React.useContext(ApiKeyContext);

  const instantWinWithTierRemaining = useSelector(
    (state: ReduxState) => state.instantWin.instantWinWithTierRemaining
  );
  const instantWin = instantWinWithTierRemaining?.instant_win;
  const tierRemaining = instantWinWithTierRemaining?.tier_remaining ?? [];

  const [mustSpin, setMustSpin] = React.useState(false);
  const [prizeNumber, setPrizeNumber] = React.useState(0);
  const [showLoading, setShowLoading] = React.useState(false);
  const [spinStoped, setSpinStoped] = React.useState(false);
  const [prizeCreated, setPrizeCreated] = React.useState(false);
  const router = useRouter();

  const instantWinEventLoading = useSelector((state: ReduxState) => state.instantWinEvent.loading);

  const tierKey = instantWin?.tiers?.[prizeNumber]?.key;
  const redirectUrl = router?.query?.r ?? '';

  React.useEffect(() => {
    if (!spinStoped || !prizeCreated || !tierKey) {
      return;
    }
    setMustSpin(false);
    setShowLoading(true);

    onTierKeyChange(tierKey);
    onTierModalOpen();
  }, [spinStoped, prizeCreated, tierKey, redirectUrl, router, instantWin, instantWinEvent]);

  React.useEffect(() => {
    if (instantWinEventLoading) {
      return;
    }
    if (!instantWinEvent) {
      return;
    }
    setPrizeCreated(true);
  }, [instantWinEventLoading, instantWinEvent]);

  const data =
    instantWin?.tiers?.map((tier) => ({
      option: tier?.title,
      image: {
        uri: tier?.thumbnail_url ?? '',
      },
      optionSize: tier?.stock,
    })) ?? [];

  const targetTier = instantWin?.tiers
    ?.map((r, idx) => {
      const t = tierRemaining?.find((i) => r.key === i.tier_key);
      if (t && t.remaining >= 0) {
        return idx;
      }
    })
    .filter(Boolean);
  const handleSpinClick = () => {
    if (!mustSpin) {
      const newPrizeNumberIndex = Math.floor(Math.random() * (targetTier?.length || 0));
      const newPrizeNumber = targetTier?.[newPrizeNumberIndex] || 0;
      setPrizeNumber(newPrizeNumber);
      setMustSpin(true);

      const tierKeyForNewPrizeNumber = instantWin?.tiers?.[newPrizeNumber]?.key;
      dispatch(
        updateInstantWinEvent(
          apiKey,
          instantWinEvent.id as string,
          {
            tier_key: tierKeyForNewPrizeNumber as string,
            status: 'INSTANT_WIN_EVENT_DRAWN',
          },
          i18n.language
        )
      );
    }
  };

  return (
    <div className={clsx(styles['drawtype__wrapper'])}>
      {showLoading && (
        <div className={clsx(styles['loading__wrapper'])}>
          <CircularProgress />
        </div>
      )}
      <div className={rouletteStyles.wheel}>
        <Wheel
          mustStartSpinning={mustSpin}
          prizeNumber={prizeNumber}
          data={data}
          backgroundColors={['#99E6EA', '#CCF2F5', '#BDE096']}
          outerBorderColor="white"
          innerBorderColor="white"
          radiusLineColor="white"
          textColors={['#ffffff']}
          onStopSpinning={() => {
            setSpinStoped(true);
          }}
          pointerProps={{
            src: '/static/images/roulette_pointer.svg',
          }}
        />
      </div>
      <button className={rouletteStyles.startButton} onClick={handleSpinClick}>
        START
      </button>
    </div>
  );
};
